import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import ActionStatusBadge from "@/components/ActionStatusBadge";
import CorrectiveActionEditor from "@/components/CorrectiveActionEditor";
import { api, getAxiosErrorMessage } from "@/lib/api";
import {
  Plus, Loader2, Wrench, Clock, AlertTriangle, ShieldCheck, RotateCw, Send,
  Link2 as LinkIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const VERIFICATION_STYLES = {
  a_verifier: "bg-amber-50 text-amber-800 border-amber-200",
  efficace: "bg-emerald-50 text-emerald-700 border-emerald-200",
  inefficace: "bg-rose-50 text-rose-700 border-rose-200",
  a_reouvrir: "bg-blue-50 text-blue-800 border-blue-200",
};
const VERIFICATION_LABELS = {
  a_verifier: "À vérifier",
  efficace: "Efficace",
  inefficace: "Inefficace",
  a_reouvrir: "À rouvrir",
};

function VerificationBadge({ status, testId }) {
  if (!status) return null;
  return (
    <span
      data-testid={testId || `verif-badge-${status}`}
      className={cn(
        "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border",
        VERIFICATION_STYLES[status],
      )}
    >
      {VERIFICATION_LABELS[status] || status}
    </span>
  );
}

export default function CorrectiveActions() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  // Action en cours d'édition. L'édition en ligne ne couvrait que quatre champs
  // sur douze : compléter une action imposait de réécrire ce qui était prévu.
  const [enEdition, setEnEdition] = useState(null);

  const fetchList = async () => {
    setLoading(true);
    const { data } = await api.get("/corrective-actions");
    setItems(data);
    setLoading(false);
  };

  useEffect(() => {
    fetchList();
    // Liste minimale (id + nom) : /users est réservé aux admins.
    api.get("/users/assignable").then((r) => setUsers(r.data));
    api.get("/complaints").then((r) => setComplaints(r.data));
  }, []);

  const today = new Date().toISOString().slice(0, 10);

  const updateField = async (id, patch) => {
    try {
      await api.patch(`/corrective-actions/${id}`, patch);
      fetchList();
      toast.success("Mis à jour");
    } catch (e) { toast.error(getAxiosErrorMessage(e)); }
  };

  const triggerReminder = async (id) => {
    try {
      const { data } = await api.post(`/corrective-actions/${id}/verification-reminder`);
      toast.success(`Email envoyé à ${data.recipients.length} destinataire(s)`);
      fetchList();
    } catch (e) { toast.error(getAxiosErrorMessage(e)); }
  };

  return (
    <>
      <Header
        title="Actions correctives"
        subtitle="Causes, actions, efficacité (ponctuelle / permanente)"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-white/10 hover:bg-white/20 text-white border-0" data-testid="new-action-btn">
                <Plus size={16} className="mr-1.5" /> Nouvelle action
              </Button>
            </DialogTrigger>
            <ActionDialog
              users={users}
              complaints={complaints}
              onClose={() => setOpen(false)}
              onCreated={() => { setOpen(false); fetchList(); }}
            />
          </Dialog>
        }
      />

      {/* Édition d'une action existante : dialogue distinct de la création, car
          il expose l'historique, le suivi et les pièces jointes, qui n'ont pas
          de sens sur une action qui n'existe pas encore. */}
      <Dialog open={!!enEdition} onOpenChange={(v) => !v && setEnEdition(null)}>
        {enEdition ? (
          <CorrectiveActionEditor
            action={enEdition}
            users={users}
            onClose={() => setEnEdition(null)}
            onSaved={() => { setEnEdition(null); fetchList(); }}
          />
        ) : null}
      </Dialog>
      <main className="flex-1 p-4 sm:p-6 lg:p-8">
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-16 text-slate-500">
              <Wrench size={32} className="mx-auto text-slate-300 mb-3" strokeWidth={1.5} />
              Aucune action corrective.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50/60 border-b border-slate-100">
                  <tr>
                    {["Numéro", "Cause / Action", "Responsable", "Type", "Échéance", "Vérification", "Statut"].map((h, i) => (
                      <th key={i} className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {items.map((a) => {
                    const overdue = a.due_date && a.due_date.slice(0, 10) < today && a.status !== "cloturee";
                    const verifOverdue = a.verification_date && a.verification_date.slice(0, 10) < today && a.verification_status === "a_verifier";
                    return (
                      <tr key={a.id} className="hover:bg-blue-50/40 transition-colors align-top" data-testid={`action-row-${a.id}`}>
                        <td className="px-5 py-4 font-bold text-slate-900 font-heading whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => setEnEdition(a)}
                            className="rounded-md text-left text-blue-700 hover:underline"
                            data-testid={`action-open-${a.id}`}
                            title="Ouvrir et compléter cette action"
                          >
                            {a.number}
                          </button>
                          {/* Navigation dans les deux sens : depuis le litige on
                              voit ses actions, depuis l'action on revient au
                              dossier. Le numéro seul obligeait à le recopier
                              dans la recherche. */}
                          {a.complaint_number ? (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/litiges/${a.complaint_id}`);
                              }}
                              className="mt-0.5 inline-flex items-center gap-1 text-xs font-normal text-blue-700 hover:underline"
                              data-testid={`action-complaint-link-${a.id}`}
                              title="Ouvrir le litige associé"
                            >
                              <LinkIcon size={11} strokeWidth={1.75} />
                              Litige associé : {a.complaint_number}
                            </button>
                          ) : null}
                        </td>
                        <td className="px-5 py-4 max-w-md">
                          <div className="text-xs text-slate-400 uppercase tracking-wider font-bold mb-0.5">Cause</div>
                          <div className="text-sm text-slate-700 mb-2 line-clamp-2">{a.cause}</div>
                          <div className="text-xs text-slate-400 uppercase tracking-wider font-bold mb-0.5">Action</div>
                          <div className="text-sm text-slate-900 line-clamp-2">{a.action}</div>
                        </td>
                        <td className="px-5 py-4 text-slate-700 whitespace-nowrap">{a.responsible_name || "—"}</td>
                        <td className="px-5 py-4 whitespace-nowrap">
                          <Select
                            value={a.resolution_type || "ponctuelle"}
                            onValueChange={(v) => updateField(a.id, { resolution_type: v })}
                          >
                            <SelectTrigger className="w-[130px] h-8 rounded-lg text-xs border-slate-200" data-testid={`action-resolution-${a.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="ponctuelle">Ponctuelle</SelectItem>
                              <SelectItem value="permanente">Permanente</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap">
                          <span className={cn("inline-flex items-center gap-1.5 text-sm font-medium", overdue ? "text-rose-600" : "text-slate-700")}>
                            {overdue ? <AlertTriangle size={13} /> : <Clock size={13} strokeWidth={1.5} />}
                            {a.due_date?.slice(0, 10)}
                          </span>
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap">
                          {a.resolution_type === "permanente" ? (
                            <div className="space-y-1">
                              <Input
                                type="date"
                                value={a.verification_date?.slice(0, 10) || ""}
                                onChange={(e) => updateField(a.id, { verification_date: e.target.value })}
                                className="h-8 w-[140px] rounded-lg text-xs"
                                data-testid={`action-verif-date-${a.id}`}
                              />
                              <div className="flex items-center gap-1">
                                <Select
                                  value={a.verification_status || "a_verifier"}
                                  onValueChange={(v) => updateField(a.id, { verification_status: v })}
                                >
                                  <SelectTrigger className="w-[130px] h-7 rounded-lg text-xs border-slate-200" data-testid={`action-verif-status-${a.id}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="a_verifier">À vérifier</SelectItem>
                                    <SelectItem value="efficace">Efficace</SelectItem>
                                    <SelectItem value="inefficace">Inefficace</SelectItem>
                                    <SelectItem value="a_reouvrir">À rouvrir</SelectItem>
                                  </SelectContent>
                                </Select>
                                {verifOverdue ? (
                                  <span title="Vérification en retard">
                                    <RotateCw size={12} className="text-amber-600" />
                                  </span>
                                ) : null}
                              </div>
                              {(a.verification_status === "a_verifier") && a.verification_date ? (
                                <div className="flex items-center gap-1.5">
                                  <button
                                    type="button"
                                    onClick={() => triggerReminder(a.id)}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold text-blue-700 hover:bg-blue-50 transition-colors"
                                    data-testid={`action-send-reminder-${a.id}`}
                                    title="Envoyer l'email de vérification d'efficacité"
                                  >
                                    <Send size={10} /> Relancer
                                  </button>
                                  {(a.verification_reminders_count || 0) > 0 ? (
                                    <span className="text-[10px] text-slate-500">
                                      · {a.verification_reminders_count} relance(s)
                                    </span>
                                  ) : null}
                                </div>
                              ) : null}
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400">—</span>
                          )}
                        </td>
                        <td className="px-5 py-4">
                          <Select value={a.status} onValueChange={(v) => updateField(a.id, { status: v })}>
                            <SelectTrigger className="w-[130px] h-8 rounded-lg text-xs border-slate-200" data-testid={`action-status-${a.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="ouverte">Ouverte</SelectItem>
                              <SelectItem value="en_cours">En cours</SelectItem>
                              <SelectItem value="cloturee">Clôturée</SelectItem>
                              <SelectItem value="inefficace">Inefficace</SelectItem>
                            </SelectContent>
                          </Select>
                          <div className="mt-1"><ActionStatusBadge status={a.status} /></div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </>
  );
}

function ActionDialog({ users, complaints, onClose, onCreated }) {
  const [form, setForm] = useState({
    cause: "", action: "", responsible_id: "none",
    due_date: new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10),
    complaint_id: "none", status: "ouverte",
    resolution_type: "ponctuelle",
    verification_date: "",
    verification_status: "a_verifier",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        cause: form.cause,
        action: form.action,
        responsible_id: form.responsible_id === "none" ? null : form.responsible_id,
        due_date: form.due_date,
        complaint_id: form.complaint_id === "none" ? null : form.complaint_id,
        status: form.status,
        resolution_type: form.resolution_type,
        verification_date: form.resolution_type === "permanente" ? (form.verification_date || null) : null,
        verification_status: form.resolution_type === "permanente" ? form.verification_status : null,
      };
      await api.post("/corrective-actions", payload);
      toast.success("Action corrective créée");
      onCreated();
    } catch (err) { toast.error(getAxiosErrorMessage(err)); }
    finally { setSaving(false); }
  };

  return (
    <DialogContent className="max-w-2xl rounded-2xl">
      <DialogHeader><DialogTitle className="font-heading">Nouvelle action corrective</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label>Cause</Label>
          <Textarea value={form.cause} onChange={(e) => set("cause", e.target.value)} required rows={2} className="rounded-xl mt-1.5" data-testid="action-cause-input" />
        </div>
        <div>
          <Label>Action corrective</Label>
          <Textarea value={form.action} onChange={(e) => set("action", e.target.value)} required rows={3} className="rounded-xl mt-1.5" data-testid="action-action-input" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Responsable</Label>
            <Select value={form.responsible_id} onValueChange={(v) => set("responsible_id", v)}>
              <SelectTrigger className="rounded-xl mt-1.5"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">—</SelectItem>
                {users.map((u) => (<SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Date limite</Label>
            <Input type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} required className="rounded-xl mt-1.5" data-testid="action-date-input" />
          </div>
          <div>
            <Label>Type de résolution</Label>
            <Select value={form.resolution_type} onValueChange={(v) => set("resolution_type", v)}>
              <SelectTrigger className="rounded-xl mt-1.5" data-testid="action-resolution-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ponctuelle">Ponctuelle</SelectItem>
                <SelectItem value="permanente">Permanente</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.resolution_type === "permanente" ? (
            <div>
              <Label>Date de vérification d'efficacité</Label>
              <Input
                type="date"
                value={form.verification_date}
                onChange={(e) => set("verification_date", e.target.value)}
                className="rounded-xl mt-1.5"
                data-testid="action-verification-date-input"
              />
              <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                <ShieldCheck size={11} /> Relance automatique à l'approche de cette date.
              </p>
            </div>
          ) : null}
        </div>
        <div>
          <Label>Lier à un litige</Label>
          <Select value={form.complaint_id} onValueChange={(v) => set("complaint_id", v)}>
            <SelectTrigger className="rounded-xl mt-1.5"><SelectValue placeholder="Aucune" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Aucune</SelectItem>
              {complaints.map((c) => (<SelectItem key={c.id} value={c.id}>{c.number} — {c.description?.slice(0, 40)}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>Annuler</Button>
          <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl" data-testid="action-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Enregistrer
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
